/**
 * @author kostenko
 *
 * Makes a JSON-RPC request using jQuery.
 *
 * Requires at.js and constants_js.jsp
 *
 * @param method method to call
 * @param params method params
 *
 * @param [successHandler] function (data, textStatus, jqXHR) success callback function.
 *              It has the same signature as the 'success' callback of jQuery ajax() function,
 *              but the passed 'data' is not the whole AJAX response body, but a JSON-RPC response result field only.
 *              In case of JSON-RPC error, this callback is not called at all, and 'errorHandler' is invoked instead.
 *
 * @param [errorHandler] function (jqXHR, textStatus, errorThrown) error callback function.
 *              It has the same signature as the 'error' callback of jQuery.ajax() function,
 *              but is called also on JSON-RPC-level errors. In those cases, textStatus will be one of the following :
 *                rpc_sessionExpired
 *                rpc_noRights
 *                rpc_customException
 *                rpc_unexpectedError
 *              and errorThrown will be the JSON-RPC error object. For the convenience, errorThrown.exception property
 *              will contain the full name of the exception class, extracted from errorThrown.trace property.
 *
 *              NOTE: this handler is invoked also when the request is aborted by the client code.
 *              It may appear unexpected, but this is how jQuery.ajax() works, and we don't treat this situation
 *              in a special way (except as noted below).
 *
 *              The error handler must return true if the error is fully handled and no more action is needed.
 *              If any other value is returned or if there's no error handler provided at all, the built-in handler is
 *              invoked.
 *
 *              The built-in handler provides the generic behaviour for the following cases:
 *              - if the request was aborted by the client code then nothing is done
 *              - on timeout an alert is displayed
 *              - if user session has expired then an alert is shown and the page is reloaded
 *              - if the user has no required rights or account is disabled, an alert is shown and the page is reloaded
 *              - on any other error (including unexpected) an alert with the appropriate error message is shown,
 *                and a stack trace, if present, is written to the browser console.
 *
 * @param [alertErrorMessagePrefix] a string that will be shown in the beginning of error alerts
 *
 * @param [timeout] timeout in ms (default is 60000)
 *
 * @return the jqXHR object
 *
 * @name PRODUCT_NAME (defined in constants_js.jsp)
 * @name RPC_LINK (defined in constants_js.jsp)
 * @name JQUERY_RPC_ERROR_MESSAGES (defined in constants_js.jsp)
 */
export default function makeRpcCall( method, params, successHandler, errorHandler, alertErrorMessagePrefix, timeout )
{
    if (!successHandler)
        successHandler = function(){};

    if (!errorHandler)
        errorHandler = function(){};

    if (alertErrorMessagePrefix)
        alertErrorMessagePrefix += "\n\n";
    else
        alertErrorMessagePrefix = "";

    var alertWithPrefix = function (msg) {
        alert(alertErrorMessagePrefix + msg);
    };
    
    var JSON_RPC_ERROR_EXCEPTION = 490;
    var JSON_RPC_ERROR_NO_SUCH_METHOD = 591;
    var EXCEPTION_NO_RIGHTS = "com.actimind.actitime.services.NoRightsForRemoteCallException";
    var EXCEPTION_FUTURE_HIRE_DATE = "com.actimind.common.services.AccountHireDateInFutureException";
    const SESSION_NOT_VALID_EXCEPTION = 'com.actimind.actitime.services.SessionNotValidException'

    function showUnexpectedErrorMessage( details )
    {
        alertWithPrefix( JQUERY_RPC_ERROR_MESSAGES.UNEXPECTED_ERROR + details );
    }

    /**
     * @param jqXHR
     * @config status
     * @config statusText
     * @param textStatus
     * @param errorThrown
     * @config code
     * @config exception
     * @config trace
     */
    function combinedErrorHandler( jqXHR, textStatus, errorThrown )
    {
        if( !errorHandler(jqXHR, textStatus, errorThrown) )
        {
            switch ( textStatus ) {

                case 'abort':
                    break;  // the request was aborted by the calling code; do nothing

                case 'timeout':
                    alertWithPrefix( JQUERY_RPC_ERROR_MESSAGES.TIMEOUT );
                    break;

                case 'rpc_sessionExpired':
                case 'rpc_session_not_valid':
                    alertWithPrefix( JQUERY_RPC_ERROR_MESSAGES.SESSION_EXPIRED );
                    ExitConfirmationHandlersManager.resetExitConfirmation();
                    document.location.reload();
                    break;

                case 'rpc_noRights':
                    alertWithPrefix( JQUERY_RPC_ERROR_MESSAGES.NO_RIGHTS );
                    ExitConfirmationHandlersManager.resetExitConfirmation();
                    document.location.reload();
                    break;

                case 'rpc_future_hire_date':
                    alertWithPrefix( JQUERY_RPC_ERROR_MESSAGES.FUTURE_HIRE_DATE );
                    ExitConfirmationHandlersManager.resetExitConfirmation();
                    document.location.reload();
                    break;

                case 'rpc_customException':
                case 'rpc_unexpectedError':
                    if( errorThrown.trace )
                        console.log(errorThrown.trace);

                    showUnexpectedErrorMessage(
                            JQUERY_RPC_ERROR_MESSAGES.JSON_RPC_ERROR + " " + errorThrown.code +
                            (errorThrown.exception ? "\n" + JQUERY_RPC_ERROR_MESSAGES.EXCEPTION + " " + errorThrown.exception : "") +
                            "\n" + JQUERY_RPC_ERROR_MESSAGES.MESSAGE + " " + errorThrown.msg +
                            (errorThrown.trace ? JQUERY_RPC_ERROR_MESSAGES.MORE_INFO : "") );
                    break;

                case 'parseerror':
                    showUnexpectedErrorMessage( JQUERY_RPC_ERROR_MESSAGES.PARSE_ERROR );
                    break;

                case 'error':
                    if( jqXHR.status >= 100 && jqXHR.status < 1000 )
                    {
                        showUnexpectedErrorMessage( JQUERY_RPC_ERROR_MESSAGES.HTTP_ERROR + " " + jqXHR.status + " " + jqXHR.statusText );
                    }
                    else
                    {
                        alertWithPrefix( JQUERY_RPC_ERROR_MESSAGES.NETWORK_ERROR );
                    }
                    break;

                default:
                    showUnexpectedErrorMessage( JQUERY_RPC_ERROR_MESSAGES.TEXT_STATUS + " " + textStatus + "\n" + JQUERY_RPC_ERROR_MESSAGES.ERROR_THROWN + " " + errorThrown );
            }
        }
    }

    /**
     * @param data
     * @config result
     * @config error
     * @param textStatus
     * @param jqXHR
     */
    function combinedSuccessHandler( data, textStatus, jqXHR )
    {
        if( data.error )
        {

            switch ( data.error.code ) {

                case JSON_RPC_ERROR_NO_SUCH_METHOD:
                    // on healthy installation this most probably means that the session has expired
                    // and auto-login is off

                    combinedErrorHandler( jqXHR, 'rpc_sessionExpired', data.error );
                    break;

                case JSON_RPC_ERROR_EXCEPTION:
                    data.error.exception = getExceptionNameFromTrace( data.error.trace );

                    if(data.error.exception == EXCEPTION_NO_RIGHTS){
                        // this may also mean that the user's account is disabled, there's no way to determine it exactly
                        combinedErrorHandler( jqXHR, 'rpc_noRights', data.error );
                    }
                    else if(data.error.exception == EXCEPTION_FUTURE_HIRE_DATE){
                        combinedErrorHandler( jqXHR, 'rpc_future_hire_date', data.error );
                    }
                    else if(data.error.exception == SESSION_NOT_VALID_EXCEPTION) {
                        combinedErrorHandler( jqXHR, 'rpc_session_not_valid', data.error );
                    }
                    else
                        combinedErrorHandler( jqXHR, 'rpc_customException', data.error );
                    break;

                default:
                    combinedErrorHandler( jqXHR, 'rpc_unexpectedError', data.error );
            }
        }
        else
        {
            successHandler( data.result, textStatus, jqXHR );
        }
    }

    return $.ajax({
        dataType: 'json',
        contentType: 'application/json',
        type: 'POST',
        url: RPC_LINK,
        data: JSON.stringify({ method: method, params: params }),
        headers: { "X-RPC-Method": method },
        success: combinedSuccessHandler,
        error: combinedErrorHandler,
        timeout: timeout ? timeout : 60000
    });
}


// WEBPACK FOOTER //
// ./web/js/util/jquery_rpc.js